import { Bar, BarChart, CartesianGrid, LabelList, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { FONT_FAMILY, THEME_COLORS, images } from "../themes";
import { FlatList, Image, ScrollView, Text, TouchableOpacity, View, StyleSheet } from "react-native";
import React, { memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getRefurbCategories, getWindowDimensions, workshopRefurbCarStatus } from "../helper";

import { AntDesign } from "@expo/vector-icons";
import { GET_INVENTORY_STATS } from "../../screen/v3/WorkshopDashboard/query";
import Loader from "../../screen/v3/component/Loader";
import SingleSelectV3 from "./SingleSelectV3";
import { clientExternalV2, clientV2 } from "../../common";
import { useCallback } from "react";
import { useLazyQuery } from "@apollo/client";
import MultiSelectPicker from "@screen/v3/BudgetModule/components/MultiSelectPicker";
import PendingAllocationCarsModal from "./PendingAllocationCarsModal";
import { PurchaserContext, PurchaserInterface } from "@src/context/PurchaserContext";
import GlobalMultiSelectSearchPicker from "@src/components/GlobalMultiSelectSearchPicker";
import { isAquaEnabled } from "@src/utils/functions";

const podBarChart = [
  {
    dataKey: "waitingForParts",
    barColor: "#7D7D7D",
    labelColor: "#7D7D7D",
    title: "Waiting for Parts",
  },
  {
    dataKey: "technicianAssignmentPending",
    barColor: "rgba(249, 208, 66, 0.7)",
    labelColor: "#F9D042",
    title: "Technician Assignment Pending",
  },
  {
    dataKey: "workStartPending",
    barColor: "#FFB9B8",
    labelColor: "#FFA09E",
    title: "Work Start Pending",
  },
  {
    dataKey: "workInProgress",
    barColor: "#228B22",
    labelColor: "#228B22",
    title: "WIP",
  },
  {
    dataKey: "partReceivingPending",
    barColor: "#90EE90",
    labelColor: "#90EE90",
    title: "WIP (PNR)",
  },
  {
    dataKey: "waitingForAdhocParts",
    barColor: "#4CBB17",
    labelColor: "#4CBB17",
    title: "WIP (Adhoc)",
  },
  {
    dataKey: "joblistOnHold",
    barColor: "#6300A3",
    labelColor: "",
    title: "Joblist On Hold",
  },
  {
    dataKey: "joblistReadyToBePicked",
    barColor: "#D8BFD8",
    labelColor: "#D8BFD8",
    title: "Ready To Be Picked",
  },
  {
    dataKey: "verificationPending",
    barColor: "rgba(62, 125, 196, 0.5)",
    labelColor: "#3E7DC3",
    title: "Verification Pending",
  },
];

const workTypeBarChart = [
  {
    dataKey: "nonBreachedTatCars",
    barColor: "rgba(62, 125, 196, 0.5)",
    labelColor: "#3E7DC3",
  },
  {
    dataKey: "breachedTatCars",
    barColor: "rgba(205, 44, 57, 0.8)",
    labelColor: "#CD2C39",
  },
];

const inventoryTab = [
  {
    title: "Graphical View",
    value: "graphical",
  },
  {
    title: "Tabular View",
    value: "tabular",
  },
];

const chatData = {
  pod: [
    {
      pod: "Mechanical",
      waitingForParts: 5,
      technicianAssignmentPending: 11,
      workStartPending: 43,
      workInProgress: 20,
    },
    {
      pod: "Wheels & Tyres",
      waitingForParts: 30,
      technicianAssignmentPending: 20,
      workStartPending: 43,
      workInProgress: 20,
    },
    {
      pod: "AC & Electrical",
      waitingForParts: 30,
      technicianAssignmentPending: 20,
      workStartPending: 12,
      workInProgress: 20,
    },
    {
      pod: "Bodyshop",
      waitingForParts: 10,
      technicianAssignmentPending: 55,
      workStartPending: 23,
      workInProgress: 5,
    },
    {
      pod: "Accessories",
      waitingForParts: 5,
      technicianAssignmentPending: 11,
      workStartPending: 43,
      workInProgress: 20,
    },
  ],
  workType: [
    {
      label: "Refurb",
      data: [
        {
          category: "BCM",
          breachedTatCars: 20,
          nonBreachedTatCars: 50,
        },
        {
          category: "Assured",
          breachedTatCars: 10,
          nonBreachedTatCars: 20,
        },
        {
          category: "Luxury",
          breachedTatCars: 5,
          nonBreachedTatCars: 15,
        },
      ],
    },

    {
      label: "Rework",
      data: [
        {
          category: "BCM",
          breachedTatCars: 20,
          nonBreachedTatCars: 30,
        },
        {
          category: "Assured",
          breachedTatCars: 20,
          nonBreachedTatCars: 20,
        },
        {
          category: "Luxury",
          breachedTatCars: 5,
          nonBreachedTatCars: 15,
        },
      ],
    },

    {
      label: "Post-Sales",
      data: [
        {
          category: "BCM",
          breachedTatCars: 10,
          nonBreachedTatCars: 50,
        },
        {
          category: "Assured",
          breachedTatCars: 20,
          nonBreachedTatCars: 67,
        },
        {
          category: "Luxury",
          breachedTatCars: 20,
          nonBreachedTatCars: 36,
        },
      ],
    },

    {
      label: "Pre-Sales",
      data: [
        {
          category: "BCM",
          breachedTatCars: 20,
          nonBreachedTatCars: 50,
        },
        {
          category: "Assured",
          breachedTatCars: 20,
          nonBreachedTatCars: 50,
        },
        {
          category: "Luxury",
          breachedTatCars: 20,
          nonBreachedTatCars: 50,
        },
      ],
    },
  ],
};

var getRectanglePath = function getRectanglePath(x, y, width, height, radius: any = 0) {
  var maxRadius = Math.min(Math.abs(width) / 2, Math.abs(height) / 2);
  var ySign = height >= 0 ? 1 : -1;
  var xSign = width >= 0 ? 1 : -1;
  var clockWise: any = (height >= 0 && width >= 0) || (height < 0 && width < 0) ? 1 : 0;
  var path;

  if (maxRadius > 0 && radius instanceof Array) {
    var newRadius: any = [0, 0, 0, 0];
    for (var i = 0, len = 4; i < len; i++) {
      newRadius[i] = radius[i] > maxRadius ? maxRadius : radius[i];
    }
    path = "M".concat(x, ",").concat(y + ySign * newRadius[0]);
    if (newRadius[0] > 0) {
      path += "A "
        .concat(newRadius[0], ",")
        .concat(newRadius[0], ",0,0,")
        .concat(clockWise, ",")
        .concat(x + xSign * newRadius[0], ",")
        .concat(y);
    }
    path += "L ".concat(x + width - xSign * newRadius[1], ",").concat(y);
    if (newRadius[1] > 0) {
      path += "A "
        .concat(newRadius[1], ",")
        .concat(newRadius[1], ",0,0,")
        .concat(clockWise, ",\n        ")
        .concat(x + width, ",")
        .concat(y + ySign * newRadius[1]);
    }
    path += "L ".concat(x + width, ",").concat(y + height - ySign * newRadius[2]);
    if (newRadius[2] > 0) {
      path += "A "
        .concat(newRadius[2], ",")
        .concat(newRadius[2], ",0,0,")
        .concat(clockWise, ",\n        ")
        .concat(x + width - xSign * newRadius[2], ",")
        .concat(y + height);
    }
    path += "L ".concat(x + xSign * newRadius[3], ",").concat(y + height);
    if (newRadius[3] > 0) {
      path += "A "
        .concat(newRadius[3], ",")
        .concat(newRadius[3], ",0,0,")
        .concat(clockWise, ",\n        ")
        .concat(x, ",")
        .concat(y + height - ySign * newRadius[3]);
    }
    path += "Z";
  } else if (maxRadius > 0 && radius === +radius && radius > 0) {
    var _newRadius: any = Math.min(maxRadius, radius);
    path = "M "
      .concat(x, ",")
      .concat(y + ySign * _newRadius, "\n            A ")
      .concat(_newRadius, ",")
      .concat(_newRadius, ",0,0,")
      .concat(clockWise, ",")
      .concat(x + xSign * _newRadius, ",")
      .concat(y, "\n            L ")
      .concat(x + width - xSign * _newRadius, ",")
      .concat(y, "\n            A ")
      .concat(_newRadius, ",")
      .concat(_newRadius, ",0,0,")
      .concat(clockWise, ",")
      .concat(x + width, ",")
      .concat(y + ySign * _newRadius, "\n            L ")
      .concat(x + width, ",")
      .concat(y + height - ySign * _newRadius, "\n            A ")
      .concat(_newRadius, ",")
      .concat(_newRadius, ",0,0,")
      .concat(clockWise, ",")
      .concat(x + width - xSign * _newRadius, ",")
      .concat(y + height, "\n            L ")
      .concat(x + xSign * _newRadius, ",")
      .concat(y + height, "\n            A ")
      .concat(_newRadius, ",")
      .concat(_newRadius, ",0,0,")
      .concat(clockWise, ",")
      .concat(x, ",")
      .concat(y + height - ySign * _newRadius, " Z");
  } else {
    path = "M ".concat(x, ",").concat(y, " h ").concat(width, " v ").concat(height, " h ").concat(-width, " Z");
  }
  return path;
};

const renderWorkTypeLegend = (props) => {
  const [dataViewIndex, setDataViewIndex] = useState<number>(0);

  const onTabChange = (tabIndex) => {
    setDataViewIndex(tabIndex);
  };

  let totalBreachedTatCars = 0,
    totalCarsInRefurb = 0,
    category = props?.payload?.[0]?.label || "";

  props?.payload?.map((item) => {
    totalBreachedTatCars += Number(item?.value?.breachedTatCars || 0);
    totalCarsInRefurb += Number(item?.value?.breachedTatCars || 0) + Number(item?.value?.nonBreachedTatCars || 0);
  });

  return (
    <View style={{ marginBottom: 45, marginLeft: 30 }}>
      <Text style={{ fontFamily: FONT_FAMILY.three, fontSize: 17, marginBottom: 5 }}>{category}</Text>
      <Text style={{ fontFamily: FONT_FAMILY.two, color: "grey", fontSize: 14 }}>
        Breached TAT cars -{" "}
        <Text style={{ color: "rgb(205, 44, 57)", fontFamily: FONT_FAMILY.four }}>{totalBreachedTatCars}</Text>
      </Text>
      <Text style={{ fontFamily: FONT_FAMILY.two, color: "grey", fontSize: 14 }}>
        Total cars in refurb - <Text style={{ color: "black", fontFamily: FONT_FAMILY.four }}>{totalCarsInRefurb}</Text>
      </Text>
    </View>
  );
};

const renderPodLegend = (props) => {
  return (
    <View
      style={{
        flexDirection: "row",
        // justifyContent: "space-between",
        paddingLeft: 30,
        paddingBottom: 45,
        width: "90%",
        flexWrap: "wrap",
      }}
    >
      {podBarChart.map((item) => (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginHorizontal: 5,
          }}
        >
          <View
            style={{
              backgroundColor: item.barColor,
              width: 13,
              height: 13,
              borderRadius: 13,
            }}
          />
          <Text
            style={{
              color: "grey",
              fontFamily: FONT_FAMILY.two,
              fontSize: 16,
              marginLeft: 10,
            }}
          >
            {item.title}
          </Text>
        </View>
      ))}
    </View>
  );
};

interface CarsInInventoryProps {
  openModal: (index: number, refurbStatusKey, viewBy: string) => void;
  graphLabelToPodmapping: {
    [key: string]: { label: string; tabIndex: number; priority: number };
  };
  inventorySize?: string;
  navigation?: any;
  filters?: any[];
  setFilters?: any;
  pendingForAllocationSize?: string;
  showPendingAllocationModal?: () => void;
}

const filterOptions = [
  { label: "POD", id: "POD" },
  // { label: 'Breached TAT', id: 'BREACHED_TAT' }
];

const CarsInventoryTable = ({ data, onPress }): JSX.Element => {
  const workTypeFlex = [1.2, 1, 1.3, 1.1, 1, 1, 1.1, 1.1, 1, 1, 1];

  const AliasValueMap = {
    "Part Receiving Pending": "WIP - PNR",
    "Waiting For Adhoc Parts": "WIP - Adhoc",
    "Work In Progress": "WIP",
    "Joblist Ready to be picked": "Ready To Be Picked",
  };
  const currentFlex: any[] = workTypeFlex;
  return (
    <ScrollView style={{ width: "100%", maxHeight: 500 }}>
      <View style={{ flex: 1 }}>
        <FlatList
          data={data}
          keyExtractor={(item, index) => index.toString()}
          renderItem={({ item, index }) => {
            return (
              <View
                style={{
                  flexDirection: "row",
                  paddingVertical: 15,
                  paddingHorizontal: 20,
                  borderRadius: 10,
                  backgroundColor: index % 2 !== 0 ? "#6300A30A" : "",
                }}
              >
                {item?.map((val, idx) => {
                  const res = AliasValueMap.hasOwnProperty(val) ? AliasValueMap[val] : val;
                  return (
                    <View
                      style={{
                        flexDirection: "row",
                        flex: currentFlex[idx],
                        marginRight: 5,
                        justifyContent: idx < 1 ? "flex-start" : "flex-end",
                        alignItems: "center",
                      }}
                    >
                      {index !== 0 && res.includes("/") ? (
                        <>
                          <Text
                            style={{
                              fontFamily: "NotoSansBold",
                              fontSize: index === 0 ? 12 : 11,
                            }}
                          >
                            {res.split("/")[0]}
                          </Text>
                          <Text
                            style={{
                              fontFamily: "NotoSans",
                              fontSize: index === 0 ? 12 : 11,
                            }}
                          >
                            {"/" + res.split("/")[1]}
                          </Text>
                        </>
                      ) : (
                        <Text
                          style={{
                            fontFamily: index === 0 ? "NotoSansBold" : "NotoSans",
                            textAlign: index > 0 && idx == 0 ? "left" : "right",
                            fontSize: index === 0 ? 12 : 11,
                          }}
                        >
                          {res?.toUpperCase()}
                        </Text>
                      )}
                      {index > 0 && idx > 0 ? (
                        <TouchableOpacity
                          style={{ marginStart: 5 }}
                          onPress={() => {
                            onPress(index, data?.[0]?.[idx]);
                          }}
                        >
                          <AntDesign name="caret-right" size={10} color="black" />
                        </TouchableOpacity>
                      ) : null}
                    </View>
                  );
                })}
              </View>
            );
          }}
        />
      </View>
    </ScrollView>
  );
};

let refurbCat = getRefurbCategories(false, true);
refurbCat.pop();
const WORK_TYPE_OPTIONS = refurbCat;

const CarsInInventory = ({
  openModal,
  graphLabelToPodmapping,
  inventorySize,
  navigation,
  filters,
  setFilters,
  pendingForAllocationSize,
  showPendingAllocationModal,
}: CarsInInventoryProps) => {
  const { setMiscModal } = useContext<PurchaserInterface>(PurchaserContext);

  const [viewType, setViewType] = useState("Graphical");
  const [tabHistory, setTabHistory] = useState(["Graphical"]);
  const [selectedFilter, setSelectedFilter] = useState(filterOptions[0].id);
  const [graphData, setGraphData] = useState([]);
  const [workType, setWorkType] = useState([]);

  const workTypeRef = useRef<TouchableOpacity>(null);

  let completeFilters = [
    {
      key: "VIEW_BY",
      value: selectedFilter,
    },
    ...filters,
  ];

  window.onpopstate = () => {
    let tempHistory = [...tabHistory];
    if (tempHistory.length > 1) {
      tempHistory.pop();
      setViewType(tempHistory[tempHistory.length - 1]);
      setTabHistory(tempHistory);
    }
  };

  const { height } = getWindowDimensions();

  const onFetchInventoryStats = useCallback(
    (data) => {
      let graph = [];
      if (selectedFilter == "POD" || selectedFilter === "BREACHED_TAT") {
        graph = data?.getInventoryStats?.reduce((acc, stat, index) => {
          if (index === 0) return acc;
          if (graphLabelToPodmapping?.[stat?.[0]?.name]) {
            const obj = {};

            if (stat?.[1]?.label !== "0") obj["waitingForParts"] = stat?.[1]?.label;
            if (stat?.[2]?.label !== "0") obj["technicianAssignmentPending"] = stat?.[2]?.label;
            if (stat?.[3]?.label !== "0") obj["workStartPending"] = stat?.[3]?.label;
            if (stat?.[4]?.label !== "0") obj["partReceivingPending"] = stat?.[4]?.label;
            if (stat?.[5]?.label !== "0") obj["workInProgress"] = stat?.[5]?.label;
            if (stat?.[6]?.label !== "0") obj["joblistOnHold"] = stat?.[6]?.label;
            if (stat?.[7]?.label !== "0") obj["joblistReadyToBePicked"] = stat?.[7]?.label;
            if (stat?.[8]?.label !== "0") obj["waitingForAdhocParts"] = stat?.[8]?.label;
            if (stat?.[9]?.label !== "0") obj["verificationPending"] = stat?.[9]?.label;
            obj["priority"] = graphLabelToPodmapping?.[stat?.[0]?.name]?.priority;

            return [
              ...acc,
              {
                pod: `${graphLabelToPodmapping[stat?.[0]?.name]?.label} - (${stat?.[10]?.label})`,
                podValue: stat?.[0]?.name,
                ...obj,
              },
            ];
          } else {
            return acc;
          }
        }, []);

        graph.sort((a, b) => a?.priority - b?.priority);
        graph = graph.filter((item) => Object.keys(item).length > 3);
      }

      setGraphData(graph);
    },
    [selectedFilter, graphLabelToPodmapping]
  );

  const [getInventoryStats, { data: inventoryStats, loading, error }] = useLazyQuery(GET_INVENTORY_STATS, {
    client: clientExternalV2,
    fetchPolicy: "network-only",
    variables: {
      filters: completeFilters,
    },
    onCompleted: onFetchInventoryStats,
  });

  useEffect(() => {
    getInventoryStats({ variables: { filters: completeFilters } });
  }, [filters, selectedFilter]);

  useEffect(() => {
    if (!inventoryStats) setGraphData([]);
  }, [inventoryStats]);

  const getLabel = (value) => {
    const index = WORK_TYPE_OPTIONS.findIndex((obj) => obj.value === value);
    return WORK_TYPE_OPTIONS[index].displayName;
  };

  useEffect(() => {
    let tempFilters = navigation?.state?.params?.filters;
    if (tempFilters && tempFilters !== "") {
      let filterArr = [...JSON.parse(tempFilters)];
      let filterObj = {};
      filterArr.forEach((element) => {
        if (filterObj[element.key]) filterObj[element.key] += `, ${getLabel(element.value)}`;
        else filterObj[element.key] = getLabel(element.value);
      });
      Object.keys(filterObj).forEach((element) => {
        pickerSelect(filterObj[element], null, element, true);
      });
    }
  }, [navigation]);

  const getTableData = () => {
    let headingData = inventoryStats?.getInventoryStats ? [...inventoryStats?.getInventoryStats] : [];
    let valueData = headingData?.splice(1, headingData?.length - 1);
    let allData = [];
    if (valueData && valueData?.length > 0) {
      valueData = valueData
        ?.map((item) => {
          let tempItem = [...item];
          tempItem[0] = {
            ...tempItem[0],
            priority: graphLabelToPodmapping[item[0]?.name]?.priority,
          };
          return [...tempItem];
        })
        ?.sort((a, b) => a?.[0]?.priority - b?.[0]?.priority);
    }
    allData = [...headingData, ...valueData];
    return allData;
  };

  const tableData = useMemo(() => getTableData() || [], [inventoryStats]);

  const totalInventoryClick = () => {
    navigation.navigate("JobCardListingV3", {
      comingFrom: "WorkshopDashboard",
    });
  };

  const pendingForAllocationOnPress = () => {
    showPendingAllocationModal?.();
  };

  const CustomBar = (props) => {
    const { fill, x, y, width, height, index, customDataKey } = props;

    return (
      <path d={getRectanglePath(x, y, width, height)} cursor="pointer" stroke="#f8f3fb" strokeWidth={0.7} fill={fill}>
        {/* <title>{graphData[index][customDataKey]}</title> */}
      </path>
    );
  };

  const getFilterOptions = (arr) => {
    let filterOptions = [];
    arr?.forEach((element) => {
      filterOptions.push(element.displayName);
    });
    return filterOptions;
  };

  const filterClear = (type) => {
    let localFilters = [...filters];
    localFilters = localFilters.filter((element) => element.key !== type);
    switch (type) {
      case "WORK_TYPE":
        setWorkType([]);
        break;
    }
    setFilters(localFilters);
  };

  const findIndArr = (arr, value) => {
    let indx = arr?.findIndex((item) => item === value);
    return indx;
  };

  const checkFilterExist = (key, value) => {
    let index = filters.findIndex((element) => element.key === key && element.value === value);
    return index;
  };

  const addFilters = (data, value, hasValues) => {
    let tempData = [];
    if (hasValues) tempData = value?.split(", ");
    else {
      tempData = [...data];
      let tempInd = findIndArr(tempData, value);
      if (tempInd >= 0) tempData = tempData.filter((_, ind) => tempInd !== ind);
      else tempData.push(value);
    }
    return tempData;
  };

  const pickerSelect = (value, index, type, hasValues = false) => {
    let localFilters: any = [...filters];
    let filterList = WORK_TYPE_OPTIONS;

    switch (type) {
      case "WORK_TYPE":
        setWorkType(addFilters(workType, value, hasValues));
        break;
    }
    if (hasValues === false) {
      let filterIdx = checkFilterExist(type, filterList[index].value);
      if (filterIdx > -1) {
        localFilters = localFilters.filter((_, ind) => filterIdx !== ind);
      } else {
        localFilters.push({
          key: type,
          value: filterList[index].value,
        });
      }
      setFilters(localFilters);
    }
  };

  const workTypeOnPress = () => {
    workTypeRef.current?.measure?.((x, y, width, height, pageX, pageY) => {
      setMiscModal(true, { x: pageX, y: pageY + height + 5 }, () => (
        <GlobalMultiSelectSearchPicker
          width={150}
          coords={{ x: pageX, y: pageY + height + 5 }}
          data={getFilterOptions(WORK_TYPE_OPTIONS)}
          selectedIndices={workType}
          onItemPress={(value, index) => {
            pickerSelect(value, index, "WORK_TYPE");
            setMiscModal(false, { x: -1, y: -1 }, null);
          }}
        />
      ));
    });
  };

  return (
    <View>
      <View style={[styles.flexRow]}>
        <View>
          <Text
            style={{
              fontSize: 27,
              fontWeight: "700",
              fontFamily: FONT_FAMILY.boldV3,
              // marginTop: 45,
            }}
          >
            Cars in Inventory
          </Text>
          <View
            style={{
              flexDirection: "row",
              marginTop: 10,
              alignItems: "center",
            }}
          >
            <Text style={styles.workTypeText}>{`Work type: `}</Text>
            <TouchableOpacity style={styles.workTypeTO} onPress={workTypeOnPress} ref={workTypeRef}>
              <Text style={styles.selectedWorkTypeText}>{`${workType?.length > 0 ? workType : "All"}`}</Text>
              <AntDesign name="caret-down" size={10} color={THEME_COLORS.purple} />
            </TouchableOpacity>
            <View style={styles.verticalDivider} />
            <Text
              style={{
                fontFamily: FONT_FAMILY.primaryV3,
                fontSize: 14,
                color: THEME_COLORS.sideMenuGrey,
              }}
            >
              Total cars in inventory:{" "}
            </Text>
            <TouchableOpacity onPress={totalInventoryClick}>
              <Text
                style={{
                  fontSize: 14,
                  color: THEME_COLORS.purple,
                  fontFamily: FONT_FAMILY.mediumV3,
                  textDecorationLine: "underline",
                  textDecorationStyle: "dashed",
                }}
              >
                {inventorySize ? inventorySize : "--"} cars
              </Text>
            </TouchableOpacity>
            {isAquaEnabled?.() ? (
              <>
                <View style={styles.verticalDivider} />
                <Text style={styles.pendingForAllocationText}>{`Waiting for POD: `}</Text>
                <TouchableOpacity onPress={pendingForAllocationOnPress}>
                  <Text
                    style={styles.pendingForAllocationNumberText}
                  >{`${pendingForAllocationSize ?? "--"} cars`}</Text>
                </TouchableOpacity>
              </>
            ) : null}
          </View>
        </View>
        {/* <MultiSelectPicker disabled={loading} value={workType} onClear={filterClear} customStyle={styles.pickerCustom} onPress={pickerSelect} options={getFilterOptions(WORK_TYPE_OPTIONS)} placeholder={'Work Type'} type={'WORK_TYPE'} hideSearch={false} /> */}
      </View>

      <View
        style={{
          backgroundColor: "#6300a30d",
          borderRadius: 10,
          marginTop: 28,
          marginBottom: 45,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            padding: 30,
          }}
        >
          <View
            style={{
              alignItems: "center",
              flexDirection: "row",
              flex: 1,
            }}
          >
            <Text
              style={{
                color: THEME_COLORS.textGrey,
                fontFamily: FONT_FAMILY.primaryV3,
                marginRight: 10,
              }}
            >
              {"View by: "}
            </Text>
            <SingleSelectV3
              labelExtractor="label"
              valueExtractor="id"
              placeholder="View by"
              options={filterOptions}
              value={selectedFilter}
              setValue={(option) => {
                setSelectedFilter(option);
              }}
              noOutline
              style={{ padding: 0, margin: 0 }}
              containerStyle={{ width: "fit-content", padding: 0, margin: 0 }}
            />
          </View>
          <View style={{ flexDirection: "row" }}>
            {["Graphical", "Tabular"].map((item: string) => (
              <TouchableOpacity
                onPress={() => {
                  let tempHistory = [...tabHistory];
                  setViewType(item);
                  history.pushState(item, "", "");
                  tempHistory.push(item);
                  setTabHistory(tempHistory);
                }}
                style={{
                  borderBottomColor: viewType == item ? "#6300A3" : "rgba(125, 125, 125, 0.2)",
                  borderBottomWidth: viewType == item ? 2 : 0.5,
                  paddingHorizontal: 12,
                  paddingBottom: 5,
                }}
              >
                <Text
                  style={{
                    fontSize: 15,
                    fontFamily: FONT_FAMILY.three,
                    color: viewType == item ? "#6300A3" : "#7D7D7D",
                  }}
                >
                  {item} View
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </div>
        {loading && (
          <Loader
            styles={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              position: "absolute",
              background: "#00000030",
              zIndex: "3",
            }}
            message="Setting up the chart"
          />
        )}
        {viewType == "Tabular" ? (
          <View
            style={{
              // borderBottomColor:
              // viewType == item ? "#6300A3" : "rgba(125, 125, 125, 0.2)",
              // borderBottomWidth: viewType == item ? 2 : 0.5,
              paddingHorizontal: 12,
              paddingBottom: 5,
            }}
          >
            <View style={{ height: 500, paddingBottom: 10 }}>
              {tableData?.length > 0 ? (
                <CarsInventoryTable
                  data={tableData?.map((val) => {
                    return val?.map((value) => value.label);
                  })}
                  onPress={(podIndex, workStatusKey) => {
                    const podValue = tableData?.[podIndex]?.[0]?.name;
                    openModal(graphLabelToPodmapping[podValue]?.tabIndex ?? 0, workStatusKey, selectedFilter);
                  }}
                />
              ) : (
                <View style={styles.container}>
                  <Text style={styles.boldText}>No Data Found</Text>
                </View>
              )}
            </View>
          </View>
        ) : viewType === "Graphical" ? (
          <div style={{ fontFamily: FONT_FAMILY.two }}>
            <ResponsiveContainer width="100%" height={height * 0.7}>
              <BarChart data={graphData || []} margin={{ right: 50, bottom: 40 }}>
                <CartesianGrid alignmentBaseline="before-edge" strokeDasharray="2 2" vertical={false} />
                <XAxis dataKey="pod" fontSize={14} />
                <YAxis fontSize={13} />
                {/* <Tooltip offset={0} label={'waitingForParts'} /> */}
                <Legend content={renderPodLegend} verticalAlign="top" />
                {podBarChart.map((item, index) => (
                  <Bar
                    // ref={node => { chartBarRef.current[`podBar-${index}`] = node }}
                    dataKey={item.dataKey}
                    stackId={"pod"}
                    fill={item.barColor}
                    onClick={(data: any, i: number) => {
                      openModal(
                        graphLabelToPodmapping[data.podValue]?.tabIndex ?? 0,
                        workshopRefurbCarStatus[item.dataKey],
                        selectedFilter
                      );
                    }}
                    barSize={64}
                    shape={<CustomBar customDataKey={item.dataKey} />}
                  >
                    <LabelList
                      onClick={(data: any, i: number) => {
                        openModal(
                          graphLabelToPodmapping[data.podValue]?.tabIndex ?? 0,
                          workshopRefurbCarStatus[item.dataKey],
                          selectedFilter
                        );
                      }}
                      dataKey={item.dataKey}
                      position="right"
                      cursor={"pointer"}
                      valueAccessor={(data) => {
                        return data;
                      }}
                      onClick={(ev) => {
                        ev.preventDefault();
                        // console.log('chartBarRef', chartBarRef.current[`podBar-${index}`]?.props);
                        // if (chartBarRef.current[`podBar-${index}`]) {
                        //   chartBarRef.current[`podBar-${index}`].click();
                        // }
                        // console.log('clicked', graphData[index].podValue, workshopRefurbCarStatus[item.dataKey]);
                        // openModal(graphLabelToPodmapping[graphData[index].podValue]?.tabIndex ?? 0, workshopRefurbCarStatus[item.dataKey]);
                      }}
                      style={{
                        fill: item.labelColor,
                        fontFamily: FONT_FAMILY.four,
                        fontSize: 14,
                        cursor: "pointer",
                      }}
                    />
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-around",
              flexWrap: "wrap",
              fontFamily: FONT_FAMILY.two,
            }}
          >
            {chatData.workType.map((value, index) => (
              <ResponsiveContainer width="50%" height={400}>
                <BarChart data={value?.data || []} margin={{ right: 60, bottom: 40 }}>
                  <CartesianGrid alignmentBaseline="before-edge" strokeDasharray="2 2" vertical={false} />
                  <XAxis dataKey="category" fontSize={14} />
                  <YAxis fontSize={13} />
                  <Legend
                    content={renderWorkTypeLegend}
                    verticalAlign="top"
                    payload={value?.data?.map((item) => ({
                      value: item,
                      label: value.label,
                    }))}
                  />
                  {workTypeBarChart.map((item) => (
                    <Bar dataKey={item.dataKey} stackId={index} fill={item.barColor} cursor={"pointer"} barSize={50}>
                      <LabelList
                        dataKey={item.dataKey}
                        position="right"
                        style={{
                          fill: item.labelColor,
                          fontFamily: FONT_FAMILY.four,
                          fontSize: 14,
                        }}
                      />
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ))}
          </div>
        )}
      </View>
    </View>
  );
};

export default memo(CarsInInventory);

const styles = StyleSheet.create({
  pickerCustom: {
    marginHorizontal: 0,
    marginLeft: 20,
    marginBottom: 10,
  },
  flexRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  container: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  boldText: {
    fontFamily: "NotoSansBold",
    fontSize: 16,
    color: "#303030",
  },
  verticalDivider: {
    width: 1,
    height: 20,
    marginHorizontal: 15,
    backgroundColor: THEME_COLORS.sideMenuGrey,
  },
  pendingForAllocationText: {
    fontFamily: FONT_FAMILY.primaryV3,
    fontSize: 14,
    color: THEME_COLORS.sideMenuGrey,
  },
  pendingForAllocationNumberText: {
    fontFamily: FONT_FAMILY.mediumV3,
    fontSize: 14,
    color: THEME_COLORS.purple,
    textDecorationLine: "underline",
    textDecorationStyle: "dashed",
  },
  workTypeText: {
    fontFamily: FONT_FAMILY.primaryV3,
    fontSize: 14,
    color: THEME_COLORS.sideMenuGrey,
  },
  selectedWorkTypeText: {
    marginEnd: 4,
    fontFamily: FONT_FAMILY.mediumV3,
    fontSize: 14,
    color: THEME_COLORS.purple,
  },
  workTypeTO: {
    flexDirection: "row",
    alignItems: "center",
  },
});
